/**
 * @module Internet FAQs
 * @description
 */
import Link from "next/link";
import FAQs from "@/blocks/FAQS";
import { trackEvent } from "@/js/helpers/tracking";
import { useLocalePreference } from "@/hooks/useLocalePreference";

/**
 * @function Component
 * @description Hero that displays headline and predictive service check
 */
const Component = () => {
  const { prefersSpanish } = useLocalePreference();

  const verizonForwardHref = prefersSpanish
    ? "https://espanol.verizon.com/discounts/verizon-forward/?intcmp=ftr|shop-internetfaqs|vzforward"
    : "https://www.verizon.com/discounts/verizon-forward/?intcmp=ftr|shop-internetfaqs|vzforward";

  const affordabilityProgramsHref = prefersSpanish
    ? "https://frontier.com/discount-programs/affordability-programs?ljs=es-US"
    : "https://frontier.com/discount-programs/affordability-programs";

  /**
   * @function handleClick
   * @description
   */
  const handleClick = (event) => {
    const { target } = event;
    const text = target.innerHTML;
    const data = {
      elementType: "LINK",
      position: "FAQ",
      text,
      actionOutcome: "INTERNALLINK",
    };

    trackEvent({ action: "elementClicked", data, event });
  };

  return (
    <FAQs
      accordionData={[
        {
          title: "Is Frontier Fiber available in my neighborhood?",
          content: (
            <>
              Frontier Internet services vary by location.{" "}
              <Link
                key="link-1"
                href="/local"
                onClick={handleClick}
                aria-label="Link to local page"
              >
                Learn More
              </Link>{" "}
              about which plans are available at your address.
            </>
          ),
          contentRaw:
            "Frontier Internet services vary by location. Learn More about which plans are available at your address.",
        },
        {
          title: "Do Frontier plans require term contracts?",
          content: (
            <>
              Select plans and Visa<sup>&reg;</sup> Reward Card offers require a
              12-month commitment. If you cancel early, a prorated early
              termination fee applies.
            </>
          ),
          contentRaw:
            "Select plans and Visa® Reward Card offers require a 12-month commitment. If you cancel early, a prorated early termination fee applies.",
        },
        {
          title: "Which Frontier Internet plan is best for me?",
          content:
            "Frontier offers communications services including Internet and Home Phone. Options will vary by location, so check with us to see which specific plans may be available at your address.",
        },
        {
          title:
            "What financial assistance options are available for Frontier Home Internet?",
          content: (
            <>
              At Frontier, we believe internet access is essential in
              today&apos;s connected world, and we&apos;re committed to helping
              more households stay connected. Visit our{" "}
              <Link href={affordabilityProgramsHref} onClick={handleClick}>
                Affordability Programs
              </Link>{" "}
              page to explore available options.
              <br />
              <br />
              Now that Frontier has joined Verizon, eligible Frontier Fiber
              customers can access the{" "}
              <Link href={verizonForwardHref} onClick={handleClick}>
                Verizon Forward
              </Link>{" "}
              discount. New and existing customers who participate in qualifying
              assistance programs, such as Lifeline, SNAP, or WIC, or who have
              received a Federal Pell Grant within the past year, may be
              eligible.
            </>
          ),
          contentRaw:
            "At Frontier, we believe internet access is essential in today's connected world, and we're committed to helping more households stay connected. Visit our Affordability Programs page to explore available options. Now that Frontier has joined Verizon, eligible Frontier Fiber customers can access the Verizon Forward discount. New and existing customers who participate in qualifying assistance programs, such as Lifeline, SNAP, or WIC, or who have received a Federal Pell Grant within the past year, may be eligible.",
        },
      ]}
    />
  );
};

export default Component;
